import React from "react";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import Button from "@mui/material/Button";

import Modal from "fond/widgets/Modal";
import NonIdealState from "fond/widgets/NonIdealState";

interface NetworkErrorModalProps {
  /**
   * Callback fired when the user clicks the Reload button
   * Typically this should refetch the data or reload the page
   */
  onReload: () => void;
  /**
   * Custom error message (optional)
   * @default "Unable to connect to the server. Please check your internet connection and try again."
   */
  message?: string;
  /**
   * Whether the modal is open
   * @default true
   */
  open?: boolean;
}

/**
 * Modal that displays a network connectivity error with a reload action.
 *
 * This should be shown when detecting network failures (offline, timeout, connection lost)
 * rather than HTTP errors (404, 403, etc).
 *
 * @example
 * ```tsx
 * const { data, error, refetch } = useGetProjectQuery(projectId);
 *
 * if (isNetworkError(error)) {
 *   return <NetworkErrorModal onReload={refetch} />;
 * }
 * ```
 */
const NetworkErrorModal: React.FC<NetworkErrorModalProps> = ({
  onReload,
  message = "Unable to connect to the server. Please check your internet connection and try again.",
  open = true,
}: NetworkErrorModalProps) => {
  return (
    <Modal
      className="network-error-modal"
      open={open}
      disableBackdropClick
      disableEscapeKeyDown
      content={
        <NonIdealState
          icon={<CloudOffIcon sx={{ fontSize: "4em" }} color="warning" />}
          title="Connection error"
          description={message}
          action={
            <Button variant="contained" color="primary" onClick={onReload}>
              Reload
            </Button>
          }
        />
      }
    />
  );
};

export default NetworkErrorModal;
